import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Search, Bell, Plus, Menu } from 'lucide-react';
import { useUiStore } from '@/lib/stores/ui';

interface TopbarProps {
  pageTitle: string;
  crumb?: string;
}

export function Topbar({ pageTitle, crumb }: TopbarProps) {
  const { toggleSidebar } = useUiStore();
  const [focused, setFocused] = useState(false);

  return (
    <header className="app-topbar">
      {/* Hamburger — mobile only (hidden via CSS on ≥861px) */}
      <button
        className="menu-btn"
        onClick={toggleSidebar}
        aria-label="Odpri meni"
        style={{
          width: '38px',
          height: '38px',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--app-raised)',
          border: '1px solid var(--app-line2)',
          borderRadius: 'var(--radius-sm)',
          color: 'var(--app-muted)',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <Menu size={18} strokeWidth={1.6} />
      </button>

      {/* Page title */}
      <div>
        {crumb && (
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.7rem',
            color: 'var(--app-subtle)',
            letterSpacing: '0.04em',
          }}>
            {crumb}
          </div>
        )}
        <div style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 560,
          fontSize: '1.3rem',
          color: '#fff',
        }}>
          {pageTitle}
        </div>
      </div>

      {/* Search */}
      <div style={{ marginLeft: 'auto', position: 'relative' }}>
        <Search
          size={16}
          strokeWidth={1.6}
          style={{
            position: 'absolute',
            left: '0.7rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--app-subtle)',
            pointerEvents: 'none',
          }}
        />
        <input
          type="text"
          placeholder="Išči kontakte, klice…"
          className="topbar-search-input"
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: focused ? '280px' : '240px',
            padding: '0.55rem 0.85rem 0.55rem 2.1rem',
            background: 'var(--app-raised)',
            border: `1px solid ${focused ? 'var(--copper-500)' : 'var(--app-line2)'}`,
            borderRadius: 'var(--radius-sm)',
            color: 'var(--app-text)',
            fontSize: '0.85rem',
            outline: 'none',
            transition: 'width var(--dur-base) var(--ease-out), border-color var(--dur-fast)',
          }}
        />
      </div>

      {/* Notifications */}
      <button
        aria-label="Obvestila"
        style={{
          width: '38px',
          height: '38px',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--app-raised)',
          border: '1px solid var(--app-line2)',
          borderRadius: 'var(--radius-sm)',
          color: 'var(--app-muted)',
          cursor: 'pointer',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <Bell size={18} strokeWidth={1.6} />
        <span style={{
          position: 'absolute',
          top: '8px',
          right: '9px',
          width: '7px',
          height: '7px',
          background: 'var(--copper-500)',
          borderRadius: '50%',
          border: '2px solid var(--app-raised)',
        }} />
      </button>

      {/* Nova ponudba CTA */}
      <Link
        to="/ponudbe"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.4rem',
          padding: '0.6rem 1rem',
          background: 'var(--copper-500)',
          color: '#fff',
          borderRadius: 'var(--radius-sm)',
          fontSize: '0.9rem',
          fontWeight: 540,
          textDecoration: 'none',
          flexShrink: 0,
          boxShadow: '0 8px 24px -8px rgba(180,83,9,0.30)',
          transition: 'background var(--dur-fast)',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--copper-600)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--copper-500)'; }}
      >
        <Plus size={16} strokeWidth={1.8} />
        Nova ponudba
      </Link>
    </header>
  );
}
